import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

import ProjectImageCarousel from '../../../Components/Media/ProjectImageCarousel';
import GoogleMap from '../../../Components/Geo/GoogleMap';
import Loader from '../../../Components/UI/Loader/Loader';
import { getAvailableDemandsForArtisanAPI } from '../../../Store/Actions/demandActions';
import {
  approximateDriveMinutes,
  computeMatchScore,
  distanceKm,
  formatDistance,
  getArtisanServiceLocation,
  getDemandPublicLocation,
} from '../../../Utils/geo';
import {
  getClientName,
  getProfileImage,
  getProjectDescription,
  getProjectImages,
} from '../../../Utils/legacyData';
import EstimationWindow from '../Offers/EstimationWindow';
import { normalizeRegionCode, normalizeRegionList } from '../../../Config/franceRegions';
import { artisanRegistrationMessage, isArtisanApproved } from '../../../Utils/artisanStatus';
import { useI18n } from '../../../i18n/I18nProvider';

const TYPE_LABELS = {
  window: 'Menuiserie',
  electricity: 'Électricité',
};

const INITIAL_VISIBLE_COUNT = 8;

const timestampValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const NewJobSection = ({ loading, error, userDemands = [], getDemands, artisanProfile, artisanId }) => {
  const history = useHistory();
  const { language, pick, formatDate } = useI18n();
  const [selectedDemandId, setSelectedDemandId] = useState('');
  const [activeMapDemandId, setActiveMapDemandId] = useState('');
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState('match');
  const [distanceFilter, setDistanceFilter] = useState('radius');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [mobileMode, setMobileMode] = useState('list');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const typeLabel = (type) => pick(
    TYPE_LABELS[type] || 'Travaux',
    type === 'electricity' ? 'Electrical work' : type === 'window' ? 'Joinery' : 'Work',
  );

  useEffect(() => {
    const unsubscribe = getDemands();
    return typeof unsubscribe === 'function' ? unsubscribe : undefined;
  }, [getDemands, artisanProfile?.speciality, artisanProfile?.user_type]);

  const approved = isArtisanApproved(artisanProfile?.registration_status);
  const serviceCenter = getArtisanServiceLocation(artisanProfile);
  const serviceRadius = Number(artisanProfile?.service_area?.radius_km) || 30;

  useEffect(() => {
    if (serviceCenter) return;
    setDistanceFilter('all');
    if (sortMode === 'distance') setSortMode('match');
  }, [serviceCenter, sortMode]);

  const availableDemands = useMemo(
    () => userDemands.filter((demand) => demand?.demand_status === 'unassigned'),
    [userDemands],
  );

  const enrichedDemands = useMemo(() => availableDemands.map((demand) => {
    const publicLocation = getDemandPublicLocation(demand);
    const distance = distanceKm(serviceCenter, publicLocation);
    return {
      demand,
      publicLocation,
      distance,
      driveMinutes: approximateDriveMinutes(distance),
      matchScore: computeMatchScore({ demand, artisanProfile, distance }),
    };
  }), [artisanProfile, availableDemands, serviceCenter]);

  const filteredDemands = useMemo(() => {
    const query = normalizeText(search);
    const maxDistance = distanceFilter === 'all'
      ? Infinity
      : distanceFilter === 'radius'
        ? serviceRadius
        : Number(distanceFilter);

    const filtered = enrichedDemands.filter(({ demand, distance }) => {
      if (query && !normalizeText([
        getClientName(demand),
        getProjectDescription(demand),
        demand.public_location?.label,
        demand.public_location?.city,
        demand.demand_address,
        demand.demand_region,
        TYPE_LABELS[demand.demand_type] || demand.demand_type,
        demand.demand_type === 'electricity' ? 'Electrical work' : demand.demand_type === 'window' ? 'Joinery' : 'Work',
      ].join(' ')).includes(query)) return false;

      if (Number.isFinite(maxDistance) && serviceCenter) {
        if (Number.isFinite(distance)) return distance <= maxDistance;
        // Legacy projects without coordinates remain visible when the artisan
        // explicitly accepts travel or serves the corresponding region.
        const demandRegion = normalizeRegionCode(demand.demand_region);
        const serviceRegions = normalizeRegionList(artisanProfile?.service_regions || [artisanProfile?.artisan_region]);
        return Boolean(
          artisanProfile?.nationwide_service
          || artisanProfile?.accepts_outside_regions
          || serviceRegions.includes(demandRegion)
        );
      }
      return true;
    });

    return filtered.sort((a, b) => {
      if (sortMode === 'distance') {
        return (Number.isFinite(a.distance) ? a.distance : Infinity)
          - (Number.isFinite(b.distance) ? b.distance : Infinity);
      }
      if (sortMode === 'recent') return timestampValue(b.demand.date) - timestampValue(a.demand.date);
      if (sortMode === 'oldest') return timestampValue(a.demand.date) - timestampValue(b.demand.date);
      return b.matchScore - a.matchScore || timestampValue(b.demand.date) - timestampValue(a.demand.date);
    });
  }, [artisanProfile, distanceFilter, enrichedDemands, search, serviceCenter, serviceRadius, sortMode]);

  const geoStats = useMemo(() => {
    const distances = enrichedDemands.map((item) => item.distance).filter(Number.isFinite);
    const cityCounts = enrichedDemands.reduce((counts, item) => {
      const city = item.demand?.public_location?.city || item.demand?.public_location?.label || '';
      if (city) counts[city] = (counts[city] || 0) + 1;
      return counts;
    }, {});
    const topCity = Object.entries(cityCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
    return {
      within10: distances.filter((value) => value <= 10).length,
      averageDistance: distances.length
        ? distances.reduce((sum, value) => sum + value, 0) / distances.length
        : null,
      topCity,
    };
  }, [enrichedDemands]);

  const displayedDemands = filteredDemands.slice(0, visibleCount);
  const hasMore = displayedDemands.length < filteredDemands.length;

  useEffect(() => setVisibleCount(INITIAL_VISIBLE_COUNT), [distanceFilter, search, sortMode]);

  const mapMarkers = useMemo(() => filteredDemands
    .filter((item) => item.publicLocation)
    .map((item) => ({
      id: item.demand.demand_id,
      position: item.publicLocation,
      label: Number.isFinite(item.distance)
        ? `${typeLabel(item.demand.demand_type)} · ${formatDistance(item.distance)}`
        : `${typeLabel(item.demand.demand_type)} · ${pick('localisation approximative', 'approximate location')}`,
      priceLabel: Number.isFinite(item.distance) ? formatDistance(item.distance).replace(' ', '') : '',
      demand: item.demand,
    })), [filteredDemands, pick]);

  const selectFromMap = (marker) => {
    setActiveMapDemandId(marker.id);
    setMobileMode('list');
    window.setTimeout(() => {
      document.getElementById(`explorer-demand-${marker.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  };

  const closeEstimation = () => setSelectedDemandId('');

  return (
    <Container>
      <FeedHeader>
        <HeaderCopy>
          <ResultsTitle>
            {pick(`${filteredDemands.length} projet${filteredDemands.length > 1 ? 's' : ''} compatible${filteredDemands.length > 1 ? 's' : ''}`, `${filteredDemands.length} matching project${filteredDemands.length === 1 ? '' : 's'}`)}
          </ResultsTitle>
        </HeaderCopy>

        <MobileControlRow>
          <MobileViewToggle>
            <ViewButton type="button" $active={mobileMode === 'list'} onClick={() => setMobileMode('list')}>
              <ViewListOutlinedIcon /> {pick('Liste', 'List')}
            </ViewButton>
            <ViewButton type="button" $active={mobileMode === 'map'} onClick={() => setMobileMode('map')}>
              <MapOutlinedIcon /> {pick('Carte', 'Map')}
            </ViewButton>
          </MobileViewToggle>
          <MobileFiltersButton type="button" onClick={() => setFiltersOpen((value) => !value)} aria-expanded={filtersOpen}>
            <TuneRoundedIcon /> {filtersOpen ? pick('Masquer', 'Hide') : pick('Filtres', 'Filters')}
          </MobileFiltersButton>
        </MobileControlRow>

        <Controls $open={filtersOpen}>
          <SearchField>
            <SearchRoundedIcon aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={pick('Ville, besoin ou client…', 'City, service or client…')}
              aria-label={pick('Rechercher parmi les projets disponibles', 'Search available projects')}
            />
          </SearchField>

          <FilterField $disabled={!serviceCenter}>
            <NearMeOutlinedIcon aria-hidden="true" />
            <select
              value={distanceFilter}
              onChange={(event) => setDistanceFilter(event.target.value)}
              aria-label={pick('Filtrer par distance', 'Filter by distance')}
              disabled={!serviceCenter}
            >
              <option value="radius">{pick(`Dans mon rayon (${serviceRadius} km)`, `Within my radius (${serviceRadius} km)`)}</option>
              <option value="10">{pick('À moins de 10 km', 'Within 10 km')}</option>
              <option value="25">{pick('À moins de 25 km', 'Within 25 km')}</option>
              <option value="50">{pick('À moins de 50 km', 'Within 50 km')}</option>
              <option value="100">{pick('À moins de 100 km', 'Within 100 km')}</option>
              <option value="all">{pick('Toutes les distances', 'All distances')}</option>
            </select>
          </FilterField>

          <SortField>
            <SortRoundedIcon aria-hidden="true" />
            <select value={sortMode} onChange={(event) => setSortMode(event.target.value)} aria-label={pick('Trier les projets', 'Sort projects')}>
              <option value="match">{pick('Recommandés', 'Recommended')}</option>
              <option value="distance" disabled={!serviceCenter}>{pick('Plus proches', 'Nearest')}</option>
              <option value="recent">{pick('Plus récents', 'Newest')}</option>
              <option value="oldest">{pick('Plus anciens', 'Oldest')}</option>
            </select>
          </SortField>
        </Controls>

        {serviceCenter ? (
          <GeoInsights aria-label={pick('Aperçu géographique des projets', 'Geographic project overview')}>
            <Insight><strong>{geoStats.within10}</strong><span>{pick('à moins de 10 km', 'within 10 km')}</span></Insight>
            <Insight><strong>{formatDistance(geoStats.averageDistance)}</strong><span>{pick('distance moyenne', 'average distance')}</span></Insight>
            <Insight><strong>{geoStats.topCity}</strong><span>{pick('zone la plus active', 'most active area')}</span></Insight>
          </GeoInsights>
        ) : (
          <GeoSetupNotice role="status">
            <div>
              <strong>{pick('Ajoutez votre point de départ professionnel', 'Add your professional starting point')}</strong>
              <span>{pick('Nous afficherons ensuite la distance, le temps de trajet, le tri par proximité et les alertes dans votre rayon.', 'We will then show distance, estimated travel time, proximity sorting and alerts within your radius.')}</span>
            </div>
            <ProfileSetupButton type="button" onClick={() => history.push('/profileartisan')}>
              {pick('Configurer dans Mon profil', 'Configure in My profile')}
            </ProfileSetupButton>
          </GeoSetupNotice>
        )}
      </FeedHeader>

      {!approved && (
        <ApprovalNotice role="status">
          <strong>{pick('Profil en cours de validation', 'Profile verification in progress')}</strong>
          <span>{artisanRegistrationMessage(artisanProfile?.registration_status, language)}</span>
        </ApprovalNotice>
      )}

      {loading && <LoaderWrap><Loader /></LoaderWrap>}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {!loading && availableDemands.length === 0 ? (
        <EmptyState>
          <HomeRepairServiceOutlinedIcon />
          <h3>{pick('Aucun nouveau projet compatible', 'No new matching project')}</h3>
          <p>{pick('Les nouvelles demandes de votre spécialité apparaîtront ici dès leur publication.', 'New requests matching your speciality will appear here as soon as they are published.')}</p>
        </EmptyState>
      ) : !loading && filteredDemands.length === 0 ? (
        <EmptyState>
          <SearchRoundedIcon />
          <h3>{pick('Aucun projet avec ces filtres', 'No projects match these filters')}</h3>
          <p>{pick('Élargissez le rayon, effacez la recherche ou vérifiez votre zone d’intervention.', 'Increase the radius, clear the search or check your service area.')}</p>
          <ResetButton type="button" onClick={() => { setSearch(''); setDistanceFilter('all'); }}>{pick('Afficher tous les projets', 'Show all projects')}</ResetButton>
        </EmptyState>
      ) : (
        <AirbnbLayout>
          <ListColumn $visible={mobileMode === 'list'}>
            <Cards>
              {displayedDemands.map(({ demand, distance, driveMinutes, matchScore }) => {
                const images = getProjectImages(demand);
                const clientName = getClientName(demand);
                const description = getProjectDescription(demand);
                const clientAvatar = getProfileImage(demand) || '/images/user.svg';
                const isActive = activeMapDemandId === demand.demand_id;
                const locationLabel = demand.public_location?.label || demand.demand_region || pick('Localisation non géolocalisée', 'Location not geocoded');
                const alreadyOffered = demand.already_offered === true;

                return (
                  <ProjectCard
                    id={`explorer-demand-${demand.demand_id}`}
                    key={demand.demand_id}
                    $active={isActive}
                    onMouseEnter={() => setActiveMapDemandId(demand.demand_id)}
                    onFocus={() => setActiveMapDemandId(demand.demand_id)}
                  >
                    <ProjectGallery images={images} alt={pick(`Photos du projet de ${clientName}`, `Project photos for ${clientName}`)} />

                    <CardBody>
                      <CardTopLine>
                        <TypeBadge>{typeLabel(demand.demand_type)}</TypeBadge>
                        <MatchBadge $strong={matchScore >= 75}><AutoAwesomeOutlinedIcon /> {pick(`${matchScore}% compatible`, `${matchScore}% match`)}</MatchBadge>
                      </CardTopLine>

                      <Client type="button" onClick={() => history.push('/clientprofile', { clientId: demand.user_id, fromDemandId: demand.demand_id })}>
                        <Avatar src={clientAvatar} alt={pick(`Profil de ${clientName}`, `${clientName} profile`)} />
                        <div>
                          <span>{pick('Demande de · Voir le profil', 'Request by · View profile')}</span>
                          <strong>{clientName}</strong>
                          <ClientTrust>{demand.client_average_rating == null ? pick('Voir le score', 'View score') : (Number(demand.client_average_rating) > 0 ? `★ ${Number(demand.client_average_rating).toFixed(1)}` : pick('Nouveau', 'New'))} · {demand.client_verified_projects == null ? pick('profil client', 'client profile') : pick(`${Number(demand.client_verified_projects || 0)} projet(s)`, `${Number(demand.client_verified_projects || 0)} project(s)`)}</ClientTrust>
                        </div>
                        <DateLabel><CalendarTodayOutlinedIcon /> {formatDate(demand.date)}</DateLabel>
                      </Client>

                      <Description className="project-description">{description || pick('Aucune description fournie.', 'No description provided.')}</Description>

                      {serviceCenter ? (
                        <DistanceStrip>
                          <DistanceItem><NearMeOutlinedIcon /><div><span>{pick('Distance', 'Distance')}</span><strong>{formatDistance(distance)}</strong></div></DistanceItem>
                          <DistanceItem><AccessTimeOutlinedIcon /><div><span>{pick('Trajet estimé', 'Estimated travel')}</span><strong>{driveMinutes ? `≈ ${driveMinutes} min` : pick('Localisation du chantier incomplète', 'Project location incomplete')}</strong></div></DistanceItem>
                        </DistanceStrip>
                      ) : (
                        <DistanceSetupCard type="button" onClick={() => history.push('/profileartisan')}>
                          <NearMeOutlinedIcon />
                          <div>
                            <strong>{pick('Ajoutez votre adresse pour calculer la distance', 'Add your address to calculate distance')}</strong>
                            <span>{pick('Le chantier reste visible, mais nous ne pouvons pas estimer le trajet sans votre point de départ.', 'The project remains visible, but we cannot estimate travel without your starting point.')}</span>
                          </div>
                        </DistanceSetupCard>
                      )}

                      <PublicLocation>
                        <LocationOnOutlinedIcon />
                        <div>
                          <span>{pick('Localisation approximative', 'Approximate location')}</span>
                          <strong>{locationLabel}</strong>
                          <small>{pick('L’adresse exacte est protégée jusqu’à l’acceptation de votre proposition.', 'The exact address is protected until your proposal is accepted.')}</small>
                        </div>
                      </PublicLocation>

                      <ActionArea>
                        <ActionHint>
                          {alreadyOffered
                            ? pick('Votre proposition est enregistrée. Suivez-la dans Mes missions → Propositions.', 'Your proposal is saved. Follow it in My jobs → Proposals.')
                            : !approved
                              ? pick('Vous pouvez consulter cette demande. L’envoi sera activé après validation de votre profil.', 'You can view this request. Sending a proposal will be enabled after your profile is verified.')
                              : pick('Proposez une visite et une estimation claire pour démarrer l’échange.', 'Suggest a visit and a clear estimate to start the conversation.')}
                        </ActionHint>
                        <PrimaryButton
                          type="button"
                          disabled={alreadyOffered || !approved}
                          onClick={() => approved && !alreadyOffered && setSelectedDemandId(demand.demand_id)}
                        >
                          <SendOutlinedIcon /> {alreadyOffered ? pick('Proposition envoyée', 'Proposal sent') : approved ? pick('Proposer une estimation', 'Send an estimate') : pick('Validation requise', 'Verification required')}
                        </PrimaryButton>
                      </ActionArea>
                    </CardBody>
                  </ProjectCard>
                );
              })}
            </Cards>

            {hasMore && (
              <LoadMoreButton type="button" onClick={() => setVisibleCount((count) => count + INITIAL_VISIBLE_COUNT)}>
                {pick('Afficher plus de projets', 'Show more projects')}
              </LoadMoreButton>
            )}
          </ListColumn>

          <MapColumn $visible={mobileMode === 'map'}>
            <StickyMap>
              <MapHeader>
                <div><strong>{pick('Carte des projets', 'Project map')}</strong><span>{pick(`${mapMarkers.length} localisation(s) disponible(s)`, `${mapMarkers.length} location(s) available`)}</span></div>
                <MapLegend>
                  {serviceCenter
                    ? pick(`Le cercle représente votre rayon de ${serviceRadius} km.`, `The circle shows your ${serviceRadius} km service radius.`)
                    : pick('Ajoutez votre point de départ pour afficher votre rayon.', 'Add your starting point to display your service radius.')}
                </MapLegend>
              </MapHeader>
              <ExplorerMap
                center={serviceCenter}
                radiusKm={serviceCenter ? serviceRadius : 0}
                markers={mapMarkers}
                activeId={activeMapDemandId}
                onMarkerClick={selectFromMap}
                zoom={10}
                ariaLabel={pick('Carte des projets disponibles', 'Map of available projects')}
              />
            </StickyMap>
          </MapColumn>
        </AirbnbLayout>
      )}

      <EstimationWindow
        showModal={approved && selectedDemandId ? 'open' : 'close'}
        handleClick={closeEstimation}
        demandId={selectedDemandId}
      />
    </Container>
  );
};

const Container = styled.section`width: 100%;`;
const FeedHeader = styled.header`display:grid;gap:.7rem;padding:.25rem .25rem .8rem;border-bottom:1px solid #e8edf1;margin-bottom:.8rem;`;
const HeaderCopy = styled.div`display: grid; gap: .2rem;`;
const Eyebrow = styled.span`color: #a88a5d; font-size: .72rem; font-weight: 900; letter-spacing: .09em; text-transform: uppercase;`;
const ResultsTitle = styled.h2`margin: 0; color: #111111; font-size: clamp(1.3rem,2.4vw,1.75rem);`;
const ResultsSubtitle = styled.p`margin: 0; color: #726b61; font-size: .9rem; line-height: 1.55;`;
const Controls = styled.div`
  display:grid;grid-template-columns:minmax(14rem,1fr) minmax(12rem,auto) minmax(10rem,auto);gap:.55rem;
  @media(max-width:64rem){display:${({ $open }) => ($open ? 'grid' : 'none')};grid-template-columns:1fr 1fr;padding:.55rem;border:1px solid #e5ddd2;border-radius:.9rem;background:#faf8f4;>label:first-child{grid-column:1/-1;}}
  @media(max-width:36rem){grid-template-columns:1fr;>label:first-child{grid-column:auto;}}
`;
const ControlBase = styled.label`
  min-width: 0; min-height: 3rem; display: flex; align-items: center; gap: .55rem; padding: 0 .8rem; border: 1px solid #d9e3e9; border-radius: .9rem; background: #faf8f4; color: #557184;
  &:focus-within { border-color: #0d598c; box-shadow: 0 0 0 3px rgba(13,89,140,.12); background: #fff; }
  svg { flex: 0 0 auto; font-size: 1.15rem; }
  input, select { width: 100%; min-width: 0; border: 0; outline: 0; background: transparent; color: #111111; font: inherit; }
  select { font-weight: 750; cursor: pointer; }
`;
const SearchField = styled(ControlBase)``;
const FilterField = styled(ControlBase)`
  opacity: ${({ $disabled }) => ($disabled ? .5 : 1)};
  select:disabled { cursor: not-allowed; }
`;
const SortField = styled(ControlBase)``;

const GeoInsights = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .55rem;
  @media (max-width: 64rem) { display: none; }
`;
const Insight = styled.div`
  display: grid;
  gap: .08rem;
  padding: .72rem .82rem;
  border: 1px solid #e1e8ec;
  border-radius: .85rem;
  background: #fff;
  strong { color: #111111; font-size: .92rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  span { color: #71818c; font-size: .68rem; font-weight: 800; text-transform: uppercase; }
`;
const ApprovalNotice = styled.div`
  display: grid;
  gap: .2rem;
  margin: 0 0 1rem;
  padding: .9rem 1rem;
  border: 1px solid #ead29b;
  border-radius: .9rem;
  background: #fff8e7;
  color: #6f5116;

  strong { font-size: .86rem; }
  span { font-size: .78rem; line-height: 1.5; }
`;
const GeoSetupNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0;
  padding: .85rem;
  border: 1px solid #eed69a;
  border-radius: .85rem;
  background: #fff9e9;
  color: #725417;

  > div { display: grid; gap: .18rem; }
  strong { font-size: .82rem; }
  span { font-size: .76rem; line-height: 1.5; }

  @media (max-width: 38rem) { align-items: stretch; flex-direction: column; }
`;
const ProfileSetupButton = styled.button`
  flex: 0 0 auto;
  min-height: 2.55rem;
  padding: .55rem .8rem;
  border: 0;
  border-radius: 999px;
  background: #111;
  color: #fff;
  font: inherit;
  font-size: .75rem;
  font-weight: 850;
  cursor: pointer;
`;
const MobileControlRow = styled.div`display:none;grid-template-columns:minmax(0,1fr) auto;gap:.45rem;@media(max-width:64rem){display:grid;}`;
const MobileViewToggle = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:.4rem;`;
const MobileFiltersButton = styled.button`display:flex;align-items:center;justify-content:center;gap:.35rem;min-height:2.75rem;padding:.55rem .72rem;border:1px solid #d5e0e6;border-radius:.8rem;background:#fff;color:#36556a;font:inherit;font-size:.75rem;font-weight:850;cursor:pointer;svg{font-size:1rem;}`;
const ViewButton = styled.button`display: flex; align-items: center; justify-content: center; gap: .4rem; min-height: 2.75rem; border: 1px solid ${({ $active }) => ($active ? '#111111' : '#d5e0e6')}; border-radius: .8rem; background: ${({ $active }) => ($active ? '#111111' : '#fff')}; color: ${({ $active }) => ($active ? '#fff' : '#36556a')}; font: inherit; font-weight: 850; cursor: pointer; svg { font-size: 1.05rem; }`;
const AirbnbLayout = styled.div`display: grid; grid-template-columns: minmax(0,1.08fr) minmax(23rem,.92fr); gap: 1rem; align-items: start; @media (max-width: 64rem) { grid-template-columns: 1fr; }`;
const ListColumn = styled.div`min-width: 0; @media (max-width: 64rem) { display: ${({ $visible }) => ($visible ? 'block' : 'none')}; }`;
const MapColumn = styled.aside`min-width: 0; @media (max-width: 64rem) { display: ${({ $visible }) => ($visible ? 'block' : 'none')}; }`;
const StickyMap = styled.div`position: sticky; top: 6rem; overflow: hidden; border: 1px solid #e7dfd2; border-radius: 1.2rem; background: #fff; box-shadow: 0 1rem 2.5rem rgba(0,0,0,.09);`;
const MapHeader = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: .8rem; padding: .9rem 1rem; border-bottom: 1px solid #e5ecef; div { display: grid; } strong { color: #111111; } span { color: #71818c; font-size: .72rem; }`;
const MapLegend = styled.small`max-width: 12rem; color: #637785; line-height: 1.4; text-align: right;`;
const ExplorerMap = styled(GoogleMap)`min-height: calc(100vh - 13rem); max-height: 46rem; @media (max-width:64rem) { min-height: 32rem; }`;
const Cards = styled.div`display: grid; gap: 1rem;`;
const ProjectCard = styled.article`width:100%;overflow:hidden;border:2px solid ${({ $active }) => ($active ? '#111111' : '#e7dfd2')};border-radius:1.25rem;background:#fff;box-shadow:0 .8rem 2rem rgba(0,0,0,.08);transition:border 160ms ease;`;
const ProjectGallery = styled(ProjectImageCarousel)`min-height: 17rem; aspect-ratio: 16 / 9; @media (max-width:42rem) { min-height: 14rem; aspect-ratio: 4 / 3; }`;
const CardBody = styled.div`display: grid; gap: .9rem; padding: clamp(1rem,2.5vw,1.35rem);`;
const CardTopLine = styled.div`display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .6rem;`;
const TypeBadge = styled.span`padding: .4rem .68rem; border-radius: 999px; background: #eaf3fb; color: #0a4d7d; font-size: .74rem; font-weight: 900;`;
const MatchBadge = styled.span`display: inline-flex; align-items: center; gap: .3rem; padding: .4rem .65rem; border-radius: 999px; background: ${({ $strong }) => ($strong ? '#e8f7ee' : '#f1f4f6')}; color: ${({ $strong }) => ($strong ? '#17623a' : '#536b7a')}; font-size: .72rem; font-weight: 850; svg { font-size: .95rem; }`;
const Client = styled.button`width:100%;border:0;background:transparent;padding:0;color:inherit;font:inherit;text-align:left;cursor:pointer;display: grid; grid-template-columns: auto minmax(0,1fr) auto; align-items: center; gap: .65rem; min-width: 0; div { display: grid; min-width: 0; } span { color:#726b61; font-size:.68rem; font-weight:850; letter-spacing:.06em; text-transform:uppercase; } strong { overflow:hidden; color:#111111; font-size:.98rem; text-overflow:ellipsis; white-space:nowrap; } @media(max-width:36rem){grid-template-columns:auto minmax(0,1fr); > span:last-child { grid-column:1/-1; }}`;
const Avatar = styled.img`width:2.8rem; height:2.8rem; border:2px solid #edf2f4; border-radius:50%; background:#edf2f4; object-fit:cover;`;
const ClientTrust = styled.small`color:#8a641a;font-size:.62rem;font-weight:800;text-transform:none;letter-spacing:0;`;
const DateLabel = styled.span`display:inline-flex; align-items:center; gap:.35rem; color:#726b61; font-size:.74rem; white-space:nowrap; svg{font-size:.95rem;}`;
const Description = styled.p`margin:0; color:#263f52; font-size:.95rem; line-height:1.65; white-space:pre-wrap;`;
const DistanceStrip = styled.div`display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:.65rem; @media(max-width:32rem){grid-template-columns:1fr;}`;
const DistanceItem = styled.div`display:flex; align-items:center; gap:.55rem; padding:.72rem .8rem; border-radius:.85rem; background:#eef5f8; color:#111111; div{display:grid;} span{color:#6b7d89; font-size:.65rem; font-weight:800; text-transform:uppercase;} strong{color:#111111; font-size:.85rem;} svg{font-size:1.1rem;}`;
const DistanceSetupCard = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: .65rem;
  padding: .8rem;
  border: 1px solid #ead6ad;
  border-radius: .9rem;
  background: #fffaf0;
  color: #78591c;
  font: inherit;
  text-align: left;
  cursor: pointer;
  > svg { flex: 0 0 auto; margin-top: .08rem; font-size: 1.1rem; }
  > div { display: grid; gap: .12rem; }
  strong { font-size: .82rem; }
  span { font-size: .73rem; line-height: 1.45; }
`;
const PublicLocation = styled.div`display:flex; align-items:flex-start; gap:.6rem; padding:.8rem; border:1px solid #e0e8ec; border-radius:.9rem; background:#faf8f4; color:#335167; >svg{flex:0 0 auto; margin-top:.1rem;} div{display:grid;} span{color:#726b61; font-size:.65rem; font-weight:850; letter-spacing:.05em; text-transform:uppercase;} strong{color:#213c50; font-size:.86rem;} small{margin-top:.18rem; color:#6d7d87; font-size:.68rem; line-height:1.4;}`;
const ActionArea = styled.div`display:flex; align-items:center; justify-content:space-between; gap:1rem; @media(max-width:42rem){align-items:stretch; flex-direction:column;}`;
const ActionHint = styled.p`max-width:25rem; margin:0; color:#6a7984; font-size:.78rem; line-height:1.5;`;
const PrimaryButton = styled.button`min-height:2.9rem; display:inline-flex; align-items:center; justify-content:center; gap:.45rem; padding:.7rem 1rem; border:0; border-radius:999px; background:#c0a375; color:#111111; font:inherit; font-size:.82rem; font-weight:900; cursor:pointer; box-shadow:0 .55rem 1.2rem rgba(192,163,117,.24); &:hover:not(:disabled){background:#e0b24f;} &:disabled{background:#dfe5e8;color:#6d7c86;box-shadow:none;cursor:not-allowed;} svg{font-size:1.05rem;} @media(max-width:42rem){width:100%;}`;
const LoadMoreButton = styled.button`min-height:3rem; display:flex; align-items:center; justify-content:center; margin:1.2rem auto 0; padding:.75rem 1.25rem; border:1px solid #cdd9e0; border-radius:999px; background:#fff; color:#0b4268; font:inherit; font-weight:850; cursor:pointer;`;
const EmptyState = styled.div`display:grid; justify-items:center; gap:.5rem; padding:2.5rem 1.25rem; border:1px dashed #cbd7df; border-radius:1.1rem; background:#faf8f4; color:#63737f; text-align:center; >svg{color:#9a711f; font-size:2.5rem;} h3{margin:.3rem 0 0; color:#243d50;} p{max-width:34rem; margin:0; line-height:1.6;}`;
const ResetButton = styled.button`margin-top:.5rem; padding:.65rem .95rem; border:1px solid #cbd7df; border-radius:999px; background:#fff; color:#0a4d7d; font:inherit; font-weight:800; cursor:pointer;`;
const LoaderWrap = styled.div`min-height:12rem; display:grid; place-items:center;`;
const ErrorMessage = styled.p`margin:0 0 1rem; padding:.85rem; border:1px solid #efcfcb; border-radius:.8rem; background:#fff6f5; color:#8c332c;`;

const mapStateToProps = ({ demands, firebase }) => ({
  loading: Boolean(demands?.loading),
  error: demands?.error,
  userDemands: demands?.demands || [],
  artisanProfile: firebase?.profile || {},
  artisanId: firebase?.auth?.uid || '',
});

const mapDispatchToProps = (dispatch) => ({
  getDemands: () => dispatch(getAvailableDemandsForArtisanAPI()),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewJobSection);
